import { DealType } from "src/business/deal";

export interface CreateAnonymousBookingPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  restaurantId: string;
  numberOfSeats: number;
  date: string;
  // Structured reference to the deal/event the booking is tied to (replaces the
  // free-text deal content). `null` when not associated with any deal/event.
  dealId: string | null;
  dealType: DealType | null;
  notes: string | null;
  // Table to seat the booking at, when it is created directly onto a
  // table-schedule slot. Omitted for bookings created without a table.
  assignedTableNumber?: number | null;
}

// Booking details collected up-front in the table-schedule placement flow. The
// `date` and `assignedTableNumber` are filled in from the chosen cell so the
// booking is created in a single request once a slot is picked.
export type AnonymousBookingDraft = Omit<
  CreateAnonymousBookingPayload,
  "date" | "assignedTableNumber"
>;

// Swap two bookings in a single request: the backend swaps their date, assigned
// table and offer (dealContent/eventContent) as a unit. Used when one booking is
// dropped onto a cell already occupied by another on the table-schedule board.
export interface SwapBookingsPayload {
  firstBookingId: string;
  firstIsAnonymous: boolean;
  secondBookingId: string;
  secondIsAnonymous: boolean;
}

export enum BookingStatus {
  CONFIRMED = "CONFIRMED",
  PENDING = "PENDING",
  CANCELED = "CANCELED",
  CHECKED_IN = "CHECKED_IN",
  NO_SHOW = "NO_SHOW",
}

// The legal status transitions are modeled as an xstate machine; see
// `getAllowedBookingTransitions` in `./bookingMachine`.

export interface Booking {
  id: string;
  bookingStatus: BookingStatus;
  numberOfSeats: number;
  dealContent: string | null;
  eventContent: string | null;
  userName: string;
  phone: string;
  email: string;
  isAnonymous: boolean;
  notes: string;
  bookingHour: string;
  bookingDate: string;
  // Table this booking is seated at on the table-schedule board. `null` when the
  // booking has not been assigned to a table yet.
  assignedTable: number | null;
  // Per-person no-show reputation from the backend: how many no-shows this
  // booker has recorded recently, and whether that crosses the warning
  // threshold. Defaults to `0` / `false` when the API omits them.
  noShowCount: number;
  hasNoShowWarning: boolean;
}
